import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
} from '@/components/ui/card'
import { CodeBlock } from '../CodeBlock'
import { SectionHeader } from '../SectionHeader'

export const WebhooksSection = () => (
	<section id="webhooks" className="mb-16">
		<SectionHeader id="webhooks">Webhooks</SectionHeader>

		{/* Overview */}
		<Card className="mb-8 shadow-sm dark:bg-gray-800/50">
			<CardHeader>
				<CardTitle className="text-xl font-semibold">
					Webhook Overview
				</CardTitle>
				<CardDescription className="dark:text-gray-400">
					Receive real-time notifications when new feedback is submitted
				</CardDescription>
			</CardHeader>
			<CardContent className="text-gray-700 dark:text-gray-300 space-y-4">
				<p>
					Webhooks allow you to receive HTTP notifications whenever specific events occur in your Feedbask program. This enables you to:
				</p>
				<ul className="list-disc pl-5 space-y-2">
					<li>Integrate feedback into your existing workflow tools (Slack, Discord, etc.)</li>
					<li>Automatically create tickets in your issue tracking system</li>
					<li>Build custom analytics and reporting dashboards</li>
					<li>Trigger automated responses or workflows</li>
				</ul>
				<div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
					<h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">
						Supported Events
					</h4>
					<ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
						<li>• <strong>new_bug_report</strong> - When a user reports a bug</li>
						<li>• <strong>new_feature_request</strong> - When a user requests a feature</li>
					</ul>
				</div>
			</CardContent>
		</Card>

		{/* Setting Up Webhooks */}
		<Card className="mb-8 shadow-sm dark:bg-gray-800/50">
			<CardHeader>
				<CardTitle className="text-xl font-semibold">
					Setting Up Webhooks
				</CardTitle>
			</CardHeader>
			<CardContent className="text-gray-700 dark:text-gray-300 space-y-6">
				<div className="space-y-4">
					<div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-md">
						<h5 className="font-medium mb-2">Step 1: Navigate to Settings</h5>
						<p className="text-sm">
							Go to your program dashboard and click on <strong>Settings → Integrations → Webhooks</strong>
						</p>
					</div>
					<div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-md">
						<h5 className="font-medium mb-2">Step 2: Create a New Webhook</h5>
						<p className="text-sm mb-2">Click "Add Webhook" and provide:</p>
						<ul className="text-sm list-disc pl-5 space-y-1">
							<li><strong>Name:</strong> A descriptive name for your webhook</li>
							<li><strong>URL:</strong> Your endpoint URL (must be HTTPS for production)</li>
							<li><strong>Events:</strong> Select which events should trigger this webhook</li>
						</ul>
					</div>
					<div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-md">
						<h5 className="font-medium mb-2">Step 3: Test Your Webhook</h5>
						<p className="text-sm">
							Use the "Test Webhook" button to send a test payload to your endpoint
						</p>
					</div>
				</div>
				<div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-md">
					<p className="text-sm text-yellow-800 dark:text-yellow-200">
						<strong>Security Note:</strong> Webhook URLs are encrypted before storage and all webhook payloads are sent over HTTPS.
					</p>
				</div>
			</CardContent>
		</Card>

		{/* Webhook Payload Format */}
		<Card className="mb-8 shadow-sm dark:bg-gray-800/50">
			<CardHeader>
				<CardTitle className="text-xl font-semibold">
					Webhook Payload Format
				</CardTitle>
				<CardDescription className="dark:text-gray-400">
					JSON payload structure sent to your endpoint
				</CardDescription>
			</CardHeader>
			<CardContent className="text-gray-700 dark:text-gray-300 space-y-6">
				<div className="space-y-4">
					<div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-md">
						<h5 className="font-medium mb-2">Bug Report Payload</h5>
						<CodeBlock
							code={`{
  "event_type": "new_bug_report",
  "timestamp": "2024-01-15T10:30:00.000Z",
  "webhook_id": "550e8400-e29b-41d4-a716-446655440000",
  "webhook_name": "Slack Integration",
  "program": {
    "name": "My App",
    "website_url": "https://myapp.com"
  },
  "data": {
    "id": "bug_12345",
    "title": "Button not working",
    "description": "The submit button doesn't respond when clicked",
    "severity": "medium",
    "status": "new",
    "reporter_email": "user@example.com",
    "reporter_name": "John Doe",
    "screenshot_url": "https://storage.feedbask.com/screenshots/...",
    "external_user_id": "user_789",
    "user_properties": {
      "plan": "premium",
      "company": "Acme Corp"
    },
    "user_context": {
      "page_url": "https://myapp.com/dashboard",
      "page_title": "Dashboard",
      "browser": "Chrome 120.0",
      "os": "Windows 10",
      "screen_size": "1920x1080"
    }
  }
}`}
							language="json"
							copyLabel="Copy Bug Report Payload"
							copyMessage="Bug report payload copied!"
						/>
					</div>

					<div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-md">
						<h5 className="font-medium mb-2">Feature Request Payload</h5>
						<CodeBlock
							code={`{
  "event_type": "new_feature_request",
  "timestamp": "2024-01-15T10:35:00.000Z",
  "webhook_id": "550e8400-e29b-41d4-a716-446655440000",
  "webhook_name": "Slack Integration",
  "program": {
    "name": "My App",
    "website_url": "https://myapp.com"
  },
  "data": {
    "id": "feature_67890",
    "title": "Dark mode support",
    "description": "Would love to have a dark mode option for nighttime use",
    "status": "new",
    "priority": "high",
    "upvotes": 0,
    "requester_email": "user@example.com",
    "requester_name": "Jane Smith",
    "external_user_id": "user_456",
    "user_properties": {
      "plan": "basic",
      "signup_date": "2024-01-01"
    }
  }
}`}
							language="json"
							copyLabel="Copy Feature Request Payload"
							copyMessage="Feature request payload copied!"
						/>
					</div>

					<div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-md">
						<h5 className="font-medium mb-2">Test Webhook Payload</h5>
						<CodeBlock
							code={`{
  "event_type": "test",
  "timestamp": "2024-01-15T10:40:00.000Z",
  "program_id": "prog_12345",
  "webhook_id": "550e8400-e29b-41d4-a716-446655440000",
  "webhook_name": "My Webhook",
  "data": {
    "message": "This is a test webhook from Feedbask",
    "test": true
  }
}`}
							language="json"
							copyLabel="Copy Test Payload"
							copyMessage="Test payload copied!"
						/>
					</div>
				</div>
			</CardContent>
		</Card>

		{/* Webhook Headers */}
		<Card className="mb-8 shadow-sm dark:bg-gray-800/50">
			<CardHeader>
				<CardTitle className="text-xl font-semibold">
					Webhook Headers
				</CardTitle>
				<CardDescription className="dark:text-gray-400">
					HTTP headers included with webhook requests
				</CardDescription>
			</CardHeader>
			<CardContent className="text-gray-700 dark:text-gray-300 space-y-4">
				<p>Each webhook request includes the following headers:</p>
				<div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-md">
					<CodeBlock
						code={`Content-Type: application/json
User-Agent: Feedbask-Webhook/1.0
X-Feedbask-Event: new_bug_report
X-Feedbask-Delivery: 550e8400-e29b-41d4-a716-446655440000-1705316400000-1`}
						language="http"
						copyLabel="Copy Headers"
						copyMessage="Headers copied!"
					/>
				</div>
				<ul className="list-disc pl-5 space-y-2 text-sm">
					<li><strong>X-Feedbask-Event:</strong> The event type that triggered the webhook</li>
					<li><strong>X-Feedbask-Delivery:</strong> Unique delivery ID (webhook_id-timestamp-attempt)</li>
				</ul>
			</CardContent>
		</Card>

		{/* Webhook Security & Best Practices */}
		<Card className="mb-8 shadow-sm dark:bg-gray-800/50">
			<CardHeader>
				<CardTitle className="text-xl font-semibold">
					Security & Best Practices
				</CardTitle>
			</CardHeader>
			<CardContent className="text-gray-700 dark:text-gray-300 space-y-4">
				<div className="grid md:grid-cols-2 gap-4">
					<div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700">
						<h4 className="font-semibold mb-2">
							Security
						</h4>
						<ul className="text-sm space-y-1">
							<li>• URLs encrypted at rest</li>
							<li>• HTTPS required</li>
							<li>• 10-second timeout</li>
						</ul>
					</div>

					<div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700">
						<h4 className="font-semibold mb-2">
							Best Practices
						</h4>
						<ul className="text-sm space-y-1">
							<li>• Respond with 200 OK quickly</li>
							<li>• Process asynchronously</li>
							<li>• Use delivery ID for idempotency</li>
						</ul>
					</div>
				</div>

				<div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-md">
					<p className="text-sm mb-1">
						<strong>Retry Policy:</strong> Failed webhooks retry 3 times (1s, 2s, 4s delays). 4xx errors don't retry.
					</p>
				</div>
			</CardContent>
		</Card>

		{/* Webhook Limits */}
		<Card className="mb-8 shadow-sm dark:bg-gray-800/50">
			<CardHeader>
				<CardTitle className="text-xl font-semibold">
					Webhook Limits
				</CardTitle>
			</CardHeader>
			<CardContent className="text-gray-700 dark:text-gray-300">
				<p className="mb-4">
					Webhook availability varies by plan:
				</p>
				<div className="grid md:grid-cols-3 gap-4">
					<div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700">
						<h5 className="font-semibold mb-2">Free Plan</h5>
						<p className="text-sm text-gray-600 dark:text-gray-400">Not available</p>
						<p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Upgrade to Growth to enable webhooks</p>
					</div>
					<div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700">
						<h5 className="font-semibold mb-2">Growth Plan</h5>
						<p className="text-sm">5 webhooks per program</p>
					</div>
					<div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700">
						<h5 className="font-semibold mb-2">Enterprise Plan</h5>
						<p className="text-sm">Unlimited webhooks</p>
					</div>
				</div>
			</CardContent>
		</Card>

		{/* Common Use Cases */}
		<Card className="mb-8 shadow-sm dark:bg-gray-800/50">
			<CardHeader>
				<CardTitle className="text-xl font-semibold">
					Common Use Cases
				</CardTitle>
			</CardHeader>
			<CardContent className="text-gray-700 dark:text-gray-300 space-y-4">
				<div className="grid md:grid-cols-2 gap-4">
					<div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700">
						<div className="flex items-start gap-3">
							<img 
								src="https://upload.wikimedia.org/wikipedia/commons/thumb/d/d5/Slack_icon_2019.svg/512px-Slack_icon_2019.svg.png" 
								alt="Slack" 
								className="w-8 h-8 rounded"
							/>
							<div>
								<h4 className="font-semibold mb-1">
									Slack Integration
								</h4>
								<p className="text-sm">
									Send bug reports and feature requests directly to Slack channels for team visibility
								</p>
							</div>
						</div>
					</div>
					<div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700">
						<div className="flex items-start gap-3">
							<img 
								src="https://upload.wikimedia.org/wikipedia/commons/8/8a/Jira_Logo.svg" 
								alt="Jira" 
								className="w-8 h-8 rounded object-contain"
							/>
							<div>
								<h4 className="font-semibold mb-1">
									Jira Integration
								</h4>
								<p className="text-sm">
									Automatically create Jira tickets from bug reports with all context included
								</p>
							</div>
						</div>
					</div>
					<div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700">
						<div className="flex items-start gap-3">
							<img 
								src="https://upload.wikimedia.org/wikipedia/commons/7/7e/Gmail_icon_%282020%29.svg" 
								alt="Gmail" 
								className="w-8 h-8 rounded"
							/>
							<div>
								<h4 className="font-semibold mb-1">
									Email Notifications
								</h4>
								<p className="text-sm">
									Trigger custom email workflows for high-priority feedback
								</p>
							</div>
						</div>
					</div>
					<div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700">
						<div className="flex items-start gap-3">
							<img 
								src="https://upload.wikimedia.org/wikipedia/commons/7/77/GAnalytics.svg" 
								alt="Google Analytics" 
								className="w-8 h-8 rounded"
							/>
							<div>
								<h4 className="font-semibold mb-1">
									Analytics Pipelines
								</h4>
								<p className="text-sm">
									Stream feedback data to your analytics tools for deeper insights
								</p>
							</div>
						</div>
					</div>
				</div>
			</CardContent>
		</Card>

		{/* Slack Integration Example */}
		<Card className="mb-8 shadow-sm dark:bg-gray-800/50">
			<CardHeader>
				<CardTitle className="text-xl font-semibold">
					Slack Integration Example
				</CardTitle>
				<CardDescription className="dark:text-gray-400">
					Complete implementation guide for sending Feedbask notifications to Slack
				</CardDescription>
			</CardHeader>
			<CardContent className="text-gray-700 dark:text-gray-300 space-y-6">
				{/* Step 1: Create Slack App */}
				<div className="space-y-4">
					<h4 className="font-semibold text-lg">Step 1: Set Up Slack Incoming Webhook</h4>
					<div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
						<ol className="space-y-3 text-sm">
							<li>
								<strong>1.</strong> Go to <a href="https://api.slack.com/apps" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">api.slack.com/apps</a> and click "Create New App"
							</li>
							<li>
								<strong>2.</strong> Choose "From scratch" and name your app (e.g., "Feedbask Notifications")
							</li>
							<li>
								<strong>3.</strong> Select your Slack workspace
							</li>
							<li>
								<strong>4.</strong> Navigate to "Features → Incoming Webhooks" and toggle it ON
							</li>
							<li>
								<strong>5.</strong> Click "Add New Webhook to Workspace"
							</li>
							<li>
								<strong>6.</strong> Choose a channel (e.g., #feedback or #bug-reports)
							</li>
							<li>
								<strong>7.</strong> Copy the Webhook URL (keep it secure!)
							</li>
						</ol>
					</div>
					<div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-md">
						<p className="text-sm text-yellow-800 dark:text-yellow-200">
							<strong>Security Note:</strong> Your Webhook URL contains a secret token. Store it in environment variables, never commit it to your repository.
						</p>
					</div>
				</div>

				{/* Step 2: Create Webhook Handler */}
				<div className="space-y-4">
					<h4 className="font-semibold text-lg">Step 2: Create Your Webhook Handler (Node.js/Express)</h4>
					<p className="text-sm">
						Create an endpoint to receive Feedbask webhooks and forward them to Slack with rich formatting:
					</p>
					<CodeBlock
						code={`// webhook-handler.js
const express = require('express');
const axios = require('axios');
const app = express();

// Your Slack Webhook URL (store in environment variable)
const SLACK_WEBHOOK_URL = process.env.SLACK_WEBHOOK_URL;

app.use(express.json());

// Webhook endpoint that Feedbask will call
app.post('/webhooks/feedbask', async (req, res) => {
  try {
    const { event_type, data, program } = req.body;
    
    // Build Slack message based on event type
    let slackMessage;
    
    if (event_type === 'new_bug_report') {
      slackMessage = formatBugReportForSlack(data, program);
    } else if (event_type === 'new_feature_request') {
      slackMessage = formatFeatureRequestForSlack(data, program);
    } else if (event_type === 'test') {
      slackMessage = formatTestMessageForSlack(data);
    }
    
    // Send to Slack
    if (slackMessage) {
      await axios.post(SLACK_WEBHOOK_URL, slackMessage);
    }
    
    // Always respond quickly to Feedbask
    res.status(200).json({ received: true });
  } catch (error) {
    console.error('Webhook processing error:', error);
    // Still return 200 to prevent retries if it's our error
    res.status(200).json({ received: true, error: true });
  }
});

// Format bug report using Slack Block Kit
function formatBugReportForSlack(data, program) {
  const severityEmoji = {
    critical: '🔴',
    high: '🟠',
    medium: '🟡',
    low: '🟢'
  };
  
  return {
    blocks: [
      {
        type: "header",
        text: {
          type: "plain_text",
          text: "🐛 New Bug Report",
          emoji: true
        }
      },
      {
        type: "section",
        fields: [
          {
            type: "mrkdwn",
            text: \`*Title:*\\n\${data.title}\`
          },
          {
            type: "mrkdwn",
            text: \`*Severity:*\\n\${severityEmoji[data.severity] || '⚪'} \${data.severity}\`
          },
          {
            type: "mrkdwn",
            text: \`*Reporter:*\\n\${data.reporter_name} (\${data.reporter_email})\`
          },
          {
            type: "mrkdwn",
            text: \`*Status:*\\n\${data.status}\`
          }
        ]
      },
      {
        type: "section",
        text: {
          type: "mrkdwn",
          text: \`*Description:*\\n\${data.description}\`
        }
      },
      // Add context information if available
      ...(data.user_context ? [{
        type: "context",
        elements: [
          {
            type: "mrkdwn",
            text: \`📱 \${data.user_context.browser} | 💻 \${data.user_context.os} | 📐 \${data.user_context.screen_size}\`
          }
        ]
      }] : []),
      // Add user properties if available
      ...(data.user_properties && Object.keys(data.user_properties).length > 0 ? [{
        type: "section",
        text: {
          type: "mrkdwn",
          text: \`*User Properties:*\\n\${Object.entries(data.user_properties)
            .map(([key, value]) => \`• \${key}: \${value}\`)
            .join('\\n')}\`
        }
      }] : []),
      // Add action buttons
      {
        type: "actions",
        elements: [
          ...(data.screenshot_url ? [{
            type: "button",
            text: {
              type: "plain_text",
              text: "View Screenshot",
              emoji: true
            },
            url: data.screenshot_url,
            style: "primary"
          }] : []),
          {
            type: "button",
            text: {
              type: "plain_text",
              text: "View in Feedbask",
              emoji: true
            },
            url: \`\${program.website_url}/dashboard/bugs/\${data.id}\`
          }
        ]
      },
      {
        type: "divider"
      },
      {
        type: "context",
        elements: [
          {
            type: "mrkdwn",
            text: \`From *\${program.name}* | Bug ID: \${data.id}\`
          }
        ]
      }
    ]
  };
}

// Format feature request using Slack Block Kit
function formatFeatureRequestForSlack(data, program) {
  const priorityEmoji = {
    critical: '🚨',
    high: '⚡',
    medium: '📌',
    low: '💭'
  };
  
  return {
    blocks: [
      {
        type: "header",
        text: {
          type: "plain_text",
          text: "✨ New Feature Request",
          emoji: true
        }
      },
      {
        type: "section",
        fields: [
          {
            type: "mrkdwn",
            text: \`*Title:*\\n\${data.title}\`
          },
          {
            type: "mrkdwn",
            text: \`*Priority:*\\n\${priorityEmoji[data.priority] || '📋'} \${data.priority}\`
          },
          {
            type: "mrkdwn",
            text: \`*Requester:*\\n\${data.requester_name} (\${data.requester_email})\`
          },
          {
            type: "mrkdwn",
            text: \`*Upvotes:*\\n👍 \${data.upvotes || 0}\`
          }
        ]
      },
      {
        type: "section",
        text: {
          type: "mrkdwn",
          text: \`*Description:*\\n\${data.description}\`
        }
      },
      // Add user properties if available
      ...(data.user_properties && Object.keys(data.user_properties).length > 0 ? [{
        type: "section",
        text: {
          type: "mrkdwn",
          text: \`*User Properties:*\\n\${Object.entries(data.user_properties)
            .map(([key, value]) => \`• \${key}: \${value}\`)
            .join('\\n')}\`
        }
      }] : []),
      {
        type: "actions",
        elements: [
          {
            type: "button",
            text: {
              type: "plain_text",
              text: "View in Feedbask",
              emoji: true
            },
            url: \`\${program.website_url}/dashboard/features/\${data.id}\`,
            style: "primary"
          }
        ]
      },
      {
        type: "divider"
      },
      {
        type: "context",
        elements: [
          {
            type: "mrkdwn",
            text: \`From *\${program.name}* | Feature ID: \${data.id}\`
          }
        ]
      }
    ]
  };
}

// Format test webhook message
function formatTestMessageForSlack(data) {
  return {
    blocks: [
      {
        type: "section",
        text: {
          type: "mrkdwn",
          text: "✅ *Feedbask Webhook Test*\\n\\nYour webhook integration is working correctly!"
        }
      },
      {
        type: "context",
        elements: [
          {
            type: "mrkdwn",
            text: \`Test message received at \${new Date().toISOString()}\`
          }
        ]
      }
    ]
  };
}

const PORT = process.env.PORT || 3000;
app.listen(PORT, () => {
  console.log(\`Webhook handler running on port \${PORT}\`);
});`}
						language="javascript"
						copyLabel="Copy Webhook Handler"
						copyMessage="Webhook handler code copied!"
					/>
				</div>

				{/* Step 3: Environment Variables */}
				<div className="space-y-4">
					<h4 className="font-semibold text-lg">Step 3: Configure Environment Variables</h4>
					<CodeBlock
						code={`# .env file
SLACK_WEBHOOK_URL=https://hooks.slack.com/services/T00000000/B00000000/XXXXXXXXXXXXXXXXXXXX
PORT=3000`}
						language="bash"
						copyLabel="Copy Environment Variables"
						copyMessage="Environment variables copied!"
					/>
				</div>

				{/* Step 4: Deploy and Configure */}
				<div className="space-y-4">
					<h4 className="font-semibold text-lg">Step 4: Deploy and Configure in Feedbask</h4>
					<ol className="space-y-2 text-sm">
						<li>
							<strong>1.</strong> Deploy your webhook handler to a service like Vercel, Railway, or Heroku
						</li>
						<li>
							<strong>2.</strong> Get your public endpoint URL (e.g., <code>https://your-app.herokuapp.com/webhooks/feedbask</code>)
						</li>
						<li>
							<strong>3.</strong> In Feedbask, go to Settings → Integrations → Webhooks
						</li>
						<li>
							<strong>4.</strong> Click "Add Webhook" and configure:
							<ul className="ml-4 mt-1 space-y-1">
								<li>• <strong>Name:</strong> Slack Integration</li>
								<li>• <strong>URL:</strong> Your deployed endpoint URL</li>
								<li>• <strong>Events:</strong> Select "Bug Reports" and "Feature Requests"</li>
							</ul>
						</li>
						<li>
							<strong>5.</strong> Click "Test Webhook" to verify the integration
						</li>
					</ol>
				</div>

				{/* Advanced Features */}
				<div className="space-y-4">
					<h4 className="font-semibold text-lg">Advanced Features</h4>
					<div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
						<h5 className="font-medium mb-2">Threading Support</h5>
						<p className="text-sm mb-2">Group related feedback in Slack threads:</p>
						<CodeBlock
							code={`// Store message timestamps to create threads
const messageThreads = new Map();

async function sendToSlackWithThreading(webhook, message, threadKey) {
  // Check if we have an existing thread
  const threadTs = messageThreads.get(threadKey);
  
  if (threadTs) {
    // Post as a reply in the thread
    message.thread_ts = threadTs;
  }
  
  const response = await axios.post(webhook, message);
  
  // Store the timestamp for future threading
  if (!threadTs && response.data.ts) {
    messageThreads.set(threadKey, response.data.ts);
  }
  
  return response;
}`}
							language="javascript"
							copyLabel="Copy Threading Code"
							copyMessage="Threading code copied!"
						/>
					</div>

					<div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg mt-4">
						<h5 className="font-medium mb-2">Custom Filtering</h5>
						<p className="text-sm mb-2">Route different types of feedback to different Slack channels:</p>
						<CodeBlock
							code={`// Route to different channels based on severity/priority
const SLACK_CHANNELS = {
  critical: process.env.SLACK_CRITICAL_WEBHOOK,
  high: process.env.SLACK_HIGH_PRIORITY_WEBHOOK,
  normal: process.env.SLACK_GENERAL_WEBHOOK
};

function getWebhookBySeverity(severity) {
  if (severity === 'critical') return SLACK_CHANNELS.critical;
  if (severity === 'high') return SLACK_CHANNELS.high;
  return SLACK_CHANNELS.normal;
}`}
							language="javascript"
							copyLabel="Copy Filtering Code"
							copyMessage="Filtering code copied!"
						/>
					</div>

					<div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg mt-4">
						<h5 className="font-medium mb-2">Interactive Responses</h5>
						<p className="text-sm mb-2">Add interactive buttons for quick actions:</p>
						<CodeBlock
							code={`// Add interactive buttons with callback IDs
{
  type: "actions",
  elements: [
    {
      type: "button",
      text: { type: "plain_text", text: "Assign to Me" },
      action_id: "assign_bug",
      value: data.id
    },
    {
      type: "button",
      text: { type: "plain_text", text: "Mark as Resolved" },
      action_id: "resolve_bug",
      value: data.id,
      style: "primary"
    },
    {
      type: "button",
      text: { type: "plain_text", text: "Create Jira Ticket" },
      action_id: "create_jira",
      value: data.id
    }
  ]
}`}
							language="javascript"
							copyLabel="Copy Interactive Buttons"
							copyMessage="Interactive buttons code copied!"
						/>
					</div>
				</div>

			</CardContent>
		</Card>
	</section>
)
